import { Link, useParams } from "react-router-dom";
import { GraduationCap, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONTENT = {
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: June 2026",
    body: [
      ["1. About us", "Up Smart Learning Ltd is a UK-based tutoring service providing one-to-one and small-group online tuition for GCSE and A-Level students. By using our website and booking lessons you agree to these terms."],
      ["2. Bookings & lessons", "Lessons and packages are booked and paid for in advance through our secure checkout. Package hours (8, 16 and 24-hour bundles) include the stated free lessons and are valid for 6 months from purchase. Lessons are delivered live on Google Meet at the scheduled time."],
      ["3. Payments", "All prices are shown in GBP and include any applicable taxes at checkout. Payments are processed securely by Stripe; we do not store your card details."],
      ["4. Cancellations & rescheduling", "Lessons may be rescheduled with at least 24 hours' notice. Cancellations made with less than 24 hours' notice may be charged in full. Please contact us to arrange changes."],
      ["5. Refunds", "Unused package hours may be refunded on a pro-rata basis within 14 days of purchase, less the value of any lessons taken and any 'free' lessons already used."],
      ["6. Conduct", "Students and tutors are expected to behave respectfully during lessons. We reserve the right to suspend accounts for abusive or inappropriate behaviour."],
      ["7. Liability", "While we select qualified, DBS-checked tutors and support exam preparation, we cannot guarantee specific grades or outcomes."],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: June 2026",
    body: [
      ["1. Data we collect", "We collect the information you provide when you register and book lessons: your name, email, your children's names and study level, and lesson details. Payment information is handled directly by Stripe."],
      ["2. How we use your data", "We use your data to create your account, schedule and deliver lessons, process payments, and send you service emails such as booking confirmations and lesson reminders."],
      ["3. Email communications", "We send transactional emails (welcome, reminders, password resets) necessary to provide our service. You can contact us at any time to update your preferences."],
      ["4. Data sharing", "We share data only with the providers needed to run the service (e.g. Stripe for payments, Google Meet for lessons). We never sell your personal data."],
      ["5. Data retention & rights", "You may request access to, correction of, or deletion of your personal data at any time by contacting us. We retain data only as long as necessary to provide the service and meet legal obligations."],
      ["6. Contact", "For any privacy questions, please reach us via the contact section on our homepage."],
    ],
  },
};

export default function Legal() {
  const { doc } = useParams();
  const data = CONTENT[doc] || CONTENT.terms;
  return (
    <div className="min-h-screen bg-[#EFE8D8]">
      <header className="bg-[#595C2B] text-white">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-brand">
            <img src="/logo_mark.png" alt="Up Smart Learning" className="h-8 w-8 object-contain" />
            <span className="font-display text-lg font-bold">Up Smart Learning</span>
          </Link>
          <Link to="/"><Button variant="ghost" className="text-white hover:bg-white/10 rounded-full" data-testid="legal-back-btn"><ArrowLeft className="h-4 w-4 mr-2" />Home</Button></Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-display text-4xl font-bold text-[#595C2B]">{data.title}</h1>
        <p className="text-sm text-slate-500 mt-2">{data.updated}</p>
        <div className="mt-10 space-y-8">
          {data.body.map(([h, p]) => (
            <section key={h}>
              <h2 className="font-display text-xl font-semibold text-[#3a3f2e]">{h}</h2>
              <p className="text-slate-600 mt-2 leading-relaxed">{p}</p>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
